// The key-outage domain (#5115, epic #5099): the pure verdict around the
// `key_outages` record — which holds count as outage membership, what the stored
// snapshot holds, the pick candidates the outage lists, and the plan one tick of
// #5116's lane executes. The lane is the IO shell; everything here is pure
// (`docs/standards/module-structure.md`).
//
// THE PARTITION RULE for membership: a hold counts when its WAKE is a key becoming
// usable. It does not count when the key works and throughput is the wall
// (`key_saturated`), when only the clock wakes it (`provider_capacity`), when the
// wall is the box's own router credentials (`pool_cooling`), or when the wake is not a
// key at all (`billing_paused`). Each exclusion's deciding code is cited on the
// member's absence below — this file re-derives none of the stamps; a stamp IS the
// verdict of the code that wrote it (the assigner, the resolver, the box's failure
// report, the triage key pick), reached with facts this domain cannot see.

import { z } from 'zod';
import {
  AccountIdSchema,
  type BYOKKeyId,
  type RawTaskId,
  RawTaskIdSchema,
  StoredBYOKKeyIdSchema,
  type TaskId,
  TaskIdSchema,
} from '../value-objects/ids';
import {
  type LaneFencePurpose,
  LlmRoutePurpose,
  LlmRoutePurposeAnySchema,
  PIN_ONLY_PURPOSES,
  TRIAGE_ROUTE_PURPOSE,
} from '../value-objects/job-class-routing';
import { resolveLlmFromPool } from '../value-objects/llm-credential';
import type { TaskLlmPreference } from '../value-objects/task-envelope';
import { type BYOKKey, type BYOKProvider, BYOKProviderSchema } from './byok';
import { RAW_TASK_LEASE_MS } from './raw-task';
import { TaskHoldReason } from './task';

/**
 * How an outage ended. `drained`: held work read empty through the whole
 * {@link KEY_OUTAGE_DRAIN_GRACE_MS} — the #5116 lane's only close. `picked`: #5118
 * served a held item through the outage's own candidates and closes it at once,
 * inside its own transaction. The closed vocabulary `key_outages_close_reason_ck`
 * holds in the database.
 */
export const KeyOutageCloseReason = {
  Drained: 'drained',
  Picked: 'picked',
} as const;
export type KeyOutageCloseReason = (typeof KeyOutageCloseReason)[keyof typeof KeyOutageCloseReason];
export const KeyOutageCloseReasonSchema = z.enum(KeyOutageCloseReason);

/**
 * The `tasks.hold_reason` stamps that count as outage membership — exactly the
 * five whose WAKE is a key (`hold.ts`): {@link TaskHoldReason.NoUsableKeys}
 * (assigner: a pool but zero usable keys right now),
 * {@link TaskHoldReason.CredentialCooling} (resolver: no key it may use NOW, woken
 * by `releaseHoldsOnKeyValid`), {@link TaskHoldReason.ReviewAwaitingModel} (no
 * model could run the review, same wake),
 * {@link TaskHoldReason.ProviderKeyRefused} (the box's failure report on the key
 * it ran with — what clears it is a KEY, funded/restored/re-keyed, never a retry)
 * and {@link TaskHoldReason.NoScreenKey} (the run-start screen found no
 * non-donated key to run on — the same every-key-out-of-the-pool story, scoped to
 * the screen; `releaseHoldsOnKeyValid` releases it, proven live by
 * `packages/db/src/queries/no-screen-key-hold.integration.test.ts`, with the
 * `NO_SCREEN_KEY_RECHECK_MS` / `SCREEN_UNAVAILABLE_RECHECK_MS` clocks as the
 * backstops for recoveries that fire no key event).
 *
 * Deliberately ABSENT, each with the code that decides it:
 * - `key_saturated` — every key is healthy and signing; "A key turning `valid`
 *   does NOT release it" (`hold.ts`). The wall is the account's own in-flight
 *   work, re-checked every `KEY_SATURATED_RECHECK_MS`
 *   (`apps/worker/src/dispatch/key-admission.ts`). There is nothing to pick.
 * - `selection_waiting` — the lane's Agents-page SELECTION waits on its own key while
 *   the rest of the pool may be healthy and idle (`hold.ts`). Not an account outage:
 *   "none of your keys can serve the work" would be false, and the remedy is the
 *   selection, not a pick. Counting it sent the outage notice for a single cooling
 *   selected key (review round 1, M4).
 * - `provider_capacity` — "A capacity refusal never waits for a key"
 *   (`taskFailureRependHold`, `entities/task/failure-budget.ts`). Only the clock
 *   wakes it.
 * - `pool_cooling` — the wall is the BOX's own router credentials, never a BYOK key
 *   (`apps/worker/src/dispatch/pool-cooling-gate.ts`, #5931): a claim that resolves
 *   a key already runs past it, so a row held here is either a box that cannot be
 *   sent a sealed credential or a claim that resolved none. Only the router's reset
 *   wakes it.
 * - every other member — not a model condition (area, box, seat, mode, repo env,
 *   resume).
 */
export const KEY_OUTAGE_TASK_HOLD_REASONS = [
  TaskHoldReason.NoUsableKeys,
  TaskHoldReason.CredentialCooling,
  TaskHoldReason.ReviewAwaitingModel,
  TaskHoldReason.ProviderKeyRefused,
  TaskHoldReason.NoScreenKey,
] as const;
export type KeyOutageTaskHoldReason = (typeof KEY_OUTAGE_TASK_HOLD_REASONS)[number];
export const KeyOutageTaskHoldReasonSchema = z.enum(KEY_OUTAGE_TASK_HOLD_REASONS);

/**
 * The `raw_tasks.hold_reason` stamps that count — every ask hold whose wake is a
 * key, and NOT `billing_paused` (its wake is `invoice.paid`, not a key; a pick
 * would move nothing). `no_byok` counts only on an account that HAS an org key —
 * the keyless account is excluded by `accountHasOrgKeySql`
 * (`packages/db/src/queries/key-outages.ts`) and its notice belongs to
 * #5120/#5121; on a keyed account it means every key is under `findKeyFor`'s
 * valid + verified floor (a key cooling in the DB counts as none), and it wakes on a
 * key becoming usable. A key that is there but Redis-COOLED is stored `key_cooling`
 * (migration 0342; triage writes it since A4b of #6501, `no_byok` before it — a row
 * held then keeps that stamp until its next claim re-holds it), woken the same way:
 * the cool ends, or a key verifies.
 * `model_parked` wakes when `cron.byok_provider_reconcile` un-parks the model
 * (through `releaseTenantHolds`); `key_inactive` wakes when a key is
 * re-activated; `model_unavailable` (#5110) is re-held on every failed re-check by
 * `holdOnModel`.
 *
 * Spelled as the stored strings — the DB vocabulary
 * (`raw_tasks_hold_reason_ck`) lives in `packages/db` (`RawTaskHoldReason`), which
 * depends on this package, not the reverse. `packages/db/src/queries/key-outages.ts`
 * pins this list with `satisfies readonly RawTaskHoldReason[]`, the
 * `RECOVERABLE_TRIAGE_REFUSALS` idiom (`apps/agents-manager/src/triage-refusal.ts`),
 * so a rename of any stored value fails to compile there.
 */
export const KEY_OUTAGE_ASK_HOLD_REASONS = [
  'no_byok',
  'model_parked',
  'key_inactive',
  'model_unavailable',
  'key_cooling',
] as const;
export type KeyOutageAskHoldReason = (typeof KEY_OUTAGE_ASK_HOLD_REASONS)[number];
export const KeyOutageAskHoldReasonSchema = z.enum(KEY_OUTAGE_ASK_HOLD_REASONS);

/**
 * A held item's STEP — the lane the key fence compares against.
 *
 * A TASK's step is `routePurposeForEnvelope(envelope)`
 * (`value-objects/job-class-routing.ts`), the lane its key pick resolves against.
 * An ASK's step is `'triage'`, its own name (never a route purpose: the `triage` PIN
 * is not a lane, #6388). It resolves on {@link TRIAGE_ROUTE_PURPOSE}, the lane the
 * triage key pick passes as `taskPurpose` since #5758 (`triageRoute`,
 * `apps/agents-manager`), so a key fenced to `general` is a candidate exactly as it
 * is at claim time.
 *
 * BOTH share the LITERAL `'triage'` with `LlmRoutePurpose.Triage`
 * (`value-objects/job-class-routing.ts`) — by design, not by accident. They name
 * DIFFERENT FACTS:
 *
 *   - {@link KEY_OUTAGE_ASK_STEP} names an ASK's step in the key-outage snapshot:
 *     the literal a held-ask row carries in `snapshot.held[].step`
 *     ({@link KeyOutageHeldAskSchema}) and the one value the outage grader
 *     ({@link keyOutagePickCandidates}) resolves — ON the triage LANE
 *     ({@link TRIAGE_ROUTE_PURPOSE}), never as a purpose of its own.
 *
 *   - `LlmRoutePurpose.Triage` names the triage judge's `llm_routes` PIN purpose on
 *     the Agents page (#6388). `triageRoute`
 *     (`apps/agents-manager/src/triage-key-failover.ts`) reads the org-scope row
 *     keyed on it (`TRIAGE_PIN_PURPOSE`), with a fallback to the `general` row —
 *     the LANE stays `general` either way.
 *
 * NEITHER is derived from the other, on purpose: deriving the ask step from
 * `LlmRoutePurpose.Triage` would let a rename of the PIN silently move the ask's
 * snapshot step (and the fence its candidates resolve against), and deriving the PIN
 * from the ask step would let a rename of the ASK's own step silently move the
 * judge's route row. Two names, two files, one literal — renamed together, or not at
 * all.
 *
 * Conflating the two would make a held-ask pick's `taskPurpose` either the ask's own
 * step (the `triage` PIN is not a lane, #6388 — no task runs as it) or the route's
 * pin purpose (which would move the LANE off `general` and fence every
 * general-fenced key out of the ask's candidates). Keeping them SEPARATE NAMES —
 * one literal, `'triage'`, one named in this file, one in
 * `job-class-routing.ts` — is what keeps the snapshot's ASK path on the triage lane
 * and the route's read site the only consumer of `LlmRoutePurpose.Triage`. If you
 * change either literal, update both call sites
 * (`apps/agents-manager/src/triage-key-failover.ts`, the `keyOutagePickCandidates`
 * ternary on {@link KEY_OUTAGE_ASK_STEP}) in the same change.
 */
export const KEY_OUTAGE_ASK_STEP = 'triage';

/** The task-step vocabulary: every lane fence purpose, DERIVED — never retyped. */
const KeyOutageTaskStepSchema = LlmRoutePurposeAnySchema.exclude([
  LlmRoutePurpose.Default,
  // The pin-only purposes (#6388) are no task lane; the ask step keeps its own name.
  ...PIN_ONLY_PURPOSES,
]);
export const KeyOutageStepSchema = z.union([
  KeyOutageTaskStepSchema,
  z.literal(KEY_OUTAGE_ASK_STEP),
]);
/** `LaneFencePurpose` plus {@link KEY_OUTAGE_ASK_STEP} — the union both reads derive steps into. */
export type KeyOutageStep = LaneFencePurpose | typeof KEY_OUTAGE_ASK_STEP;

/** One held TASK in the snapshot. `holdUntil` is ISO-8601 text, or null (a `no_usable_keys` hold has no window). */
export const KeyOutageHeldTaskSchema = z.object({
  kind: z.literal('task'),
  id: TaskIdSchema,
  step: KeyOutageTaskStepSchema,
  holdReason: KeyOutageTaskHoldReasonSchema,
  holdUntil: z.string().nullable(),
});
export type KeyOutageHeldTask = z.infer<typeof KeyOutageHeldTaskSchema>;

/** One held ASK in the snapshot. Its step is always {@link KEY_OUTAGE_ASK_STEP}. */
export const KeyOutageHeldAskSchema = z.object({
  kind: z.literal('ask'),
  id: RawTaskIdSchema,
  step: z.literal(KEY_OUTAGE_ASK_STEP),
  holdReason: KeyOutageAskHoldReasonSchema,
  holdUntil: z.string().nullable(),
});
export type KeyOutageHeldAsk = z.infer<typeof KeyOutageHeldAskSchema>;

export const KeyOutageHeldItemSchema = z.union([KeyOutageHeldTaskSchema, KeyOutageHeldAskSchema]);
export type KeyOutageHeldItem = z.infer<typeof KeyOutageHeldItemSchema>;

/** One PICK CANDIDATE — a `(key, model)` pair the outage's picker (#5118) may serve. */
export const KeyOutagePickCandidateSchema = z.object({
  /** The STORED `byok_keys.id` (a uuid) — see {@link StoredBYOKKeyIdSchema} (GlitchTip #111). */
  keyId: StoredBYOKKeyIdSchema,
  label: z.string().nullable(),
  provider: BYOKProviderSchema,
  model: z.string(),
});
export type KeyOutagePickCandidate = z.infer<typeof KeyOutagePickCandidateSchema>;

/** The candidates for ONE held step, in resolver rank order. */
export const KeyOutageUsableSchema = z.object({
  step: KeyOutageStepSchema,
  keys: z.array(KeyOutagePickCandidateSchema),
});
export type KeyOutageUsable = z.infer<typeof KeyOutageUsableSchema>;

/**
 * What `key_outages.snapshot` holds: every held item (UNCAPPED on purpose — the
 * epic requires the outage to list everything held, and the row is rewritten only
 * when its content changes) plus the pick candidates per held step. `held` is
 * stored canonically sorted by `(kind, step, id)` — {@link sortKeyOutageHeld} —
 * so {@link planKeyOutage}'s change test is a comparison, never a set rebuild.
 */
export const KeyOutageSnapshotSchema = z.object({
  held: z.array(KeyOutageHeldItemSchema),
  usable: z.array(KeyOutageUsableSchema),
});
export type KeyOutageSnapshot = z.infer<typeof KeyOutageSnapshotSchema>;

/** The `key_outages` row, with `snapshot` Zod-parsed at read. */
export const KeyOutageSchema = z.object({
  id: z.uuid(),
  accountId: AccountIdSchema,
  openedAt: z.date(),
  updatedAt: z.date(),
  lastHeldAt: z.date(),
  closedAt: z.date().nullable(),
  closeReason: KeyOutageCloseReasonSchema.nullable(),
  snapshot: KeyOutageSnapshotSchema,
});
export type KeyOutage = z.infer<typeof KeyOutageSchema>;

/** The canonical order of `snapshot.held`: `(kind, step, id)`, total and stable. */
export function sortKeyOutageHeld(held: readonly KeyOutageHeldItem[]): KeyOutageHeldItem[] {
  return [...held].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
    if (a.step !== b.step) return a.step < b.step ? -1 : 1;
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
  });
}

/**
 * The PICK CANDIDATES per step — what `snapshot.usable` stores and #5118's picker
 * serves from. For each step this is
 * `resolveLlmFromPool({ keys, cooledKeyIds, taskPurpose: step === KEY_OUTAGE_ASK_STEP ?
 * TRIAGE_ROUTE_PURPOSE : step, ossEligible: false }).order`, mapped to candidates: that
 * ranking already applies `isUsable` (active, `status = 'valid'`, verified, not
 * Redis-cooled, lane fence, earmark) and drops a key whose model is parked
 * (`refuseKeyModel`), so "usable now" is the EXISTING grader (epic decision),
 * never a second predicate. `ossEligible: false` matches `findKeyFor`'s
 * fail-closed default (`packages/db/src/queries/byok/selection.ts`): an outage
 * snapshot never proposes a donated key.
 *
 * A `provider_key_refused` outage may carry a NON-EMPTY candidate list — the
 * stamp is the box's failure report on the key it RAN with, not a verdict on the
 * pool. That is intended: the list is the pick.
 */
export function keyOutagePickCandidates(input: {
  keys: readonly BYOKKey[];
  cooledKeyIds?: readonly string[] | undefined;
  steps: readonly KeyOutageStep[];
}): KeyOutageUsable[] {
  return input.steps.map((step) => {
    const { order } = resolveLlmFromPool({
      keys: input.keys,
      cooledKeyIds: input.cooledKeyIds,
      // The ask's step resolves on the triage LANE, `general` — the taskPurpose the
      // triage key pick passes (#5758), so the candidates are the keys it would run on.
      taskPurpose: step === KEY_OUTAGE_ASK_STEP ? TRIAGE_ROUTE_PURPOSE : step,
      ossEligible: false,
    });
    return {
      step,
      keys: order.map((pick) => ({
        keyId: pick.key.id as BYOKKeyId,
        label: pick.key.label ?? null,
        provider: pick.key.provider as BYOKProvider,
        model: pick.model,
      })),
    };
  });
}

/** One step's held work, as #5118 and #5124 render it — the ONE count projection. */
export type KeyOutageHeldByStep = {
  step: KeyOutageStep;
  tasks: KeyOutageHeldTask[];
  asks: KeyOutageHeldAsk[];
};

/**
 * Group a snapshot's held items by step, steps in lexical order. The ONE count
 * projection #5118 (read + pick) and #5124 (MCP) render, so no surface re-derives
 * "how many tasks, how many asks" its own way.
 */
export function keyOutageHeldByStep(snapshot: KeyOutageSnapshot): KeyOutageHeldByStep[] {
  const byStep = new Map<KeyOutageStep, KeyOutageHeldByStep>();
  for (const item of snapshot.held) {
    let entry = byStep.get(item.step);
    if (entry === undefined) {
      entry = { step: item.step, tasks: [], asks: [] };
      byStep.set(item.step, entry);
    }
    if (item.kind === 'task') entry.tasks.push(item);
    else entry.asks.push(item);
  }
  return [...byStep.values()].sort((a, b) => (a.step < b.step ? -1 : a.step > b.step ? 1 : 0));
}

/**
 * How long an outage with NO held work stays open before it closes `drained`.
 *
 * THE FAILURE IT PREVENTS. Held work reads empty while one continuous outage is
 * still on in two places, and a one-minute tick that closed the outage in such a
 * gap and reopened it a tick later would make #5120-#5123 send a RESOLVED notice
 * on id A and an OPENED notice on id B — two notices nothing downstream can
 * merge. The grace must outlast the longer of the two gaps:
 *
 * - TASKS, the short gap: an expired window stays stamped and reads as re-check
 *   due (#5957); only the claim clears it, and a claim that rolls back is re-parked
 *   (`markWindowedHolds`, `packages/db/src/queries/tasks/windowed-holds.ts`) in the
 *   same pass; a box that CLAIMS a `provider_key_refused` task keeps it un-held
 *   through its setup until it fails again.
 * - ASKS, the long gap: a claimed re-check is un-held for its whole flight
 *   (`claimPendingRawTaskBatch` sets `triaged` and `holdUntil: null`), and a
 *   `triaged` ask does not count. Counting the in-flight re-check instead is
 *   rejected: once claimed, a `model_unavailable` re-check and the
 *   `reopenLockedAsk` stale-reason shape are the same row and no column tells
 *   them apart. One failing re-check cycle, worst case:
 *   1. the batch defers `triage_in_flight` behind the owner scope's triage mutex
 *      (`triageDeferDelayMs`, `apps/agents-manager/src/triage-mutex.ts`), and
 *      during a model outage that mutex is busy; it stops deferring once the
 *      earliest lease in the batch would pass
 *      (`apps/agents-manager/src/raw-task-triage.ts`): up to {@link
 *      RAW_TASK_LEASE_MS}, 15 min — imported, never restated;
 *   2. the stranded sweep re-nudges the lapsed row within one cadence: 5 min
 *      (`RAW_TASK_SWEEP_CRON`, `apps/worker/src/cron/raw-task-sweep.ts`);
 *   3. the re-claim runs its pass to the worst case, `RAW_TASK_BATCH_WORST_CASE_MS`
 *      (`apps/worker/src/consumers/raw-task-bridge.ts`), before `holdOnModel`
 *      (`apps/agents-manager/src/triage-hold.ts`) re-holds it: two rounds (the batch,
 *      then the L9b re-judge), each a screen leg of ceil(50 / 25) x 180 s and a judge
 *      call plus its corrective re-ask, each to `CLASSIFIER_CALL_TIMEOUT_MS` —
 *      2 x (360,000 + 2 x 514,270) = 2,777,080 ms. The hold can land at the end of
 *      the re-judge's re-ask (`corrective_reask_failed`), so the whole pass counts.
 *
 *   That totals 900,000 + 300,000 + 2,777,080 = 3,977,080 ms (about 66.3 min);
 *   75 minutes leaves about 8.7 min for the bridge drain and #5116's one-minute tick.
 *   RE-DERIVED 2026-10-08 (deep sweep 07-34): step 3 counted one judgement
 *   (874,270 ms), but a pass judges up to four times, so the 40-minute grace covered
 *   a cycle only when the pass made one judge call, and a slower one closed the
 *   outage `drained` mid-cycle and reopened it under a new id. Earlier, 2026-09-28:
 *   the reasoning default raised the judge deadline from 262,850 ms and the intake
 *   screen added its waves (30 to 40 minutes).
 *
 * RESIDUAL, named: the bound is PER RE-CHECK CYCLE. A batch that defers behind
 * the mutex a SECOND time repeats step 1 and can outrun the grace; that outage
 * then closes `drained` and reopens under a new id.
 *
 * TRIP CONDITION: anything that lengthens the claimed window re-derives this
 * constant — #5112 (fail over to another key) and #5114 (hold instead of ending)
 * adding judge calls to one claim, a longer {@link RAW_TASK_LEASE_MS} (the only
 * compile-linked term: `packages/domain` depends on `zod` alone, so the sweep
 * cadence and the batch worst case are named by path above), a slower sweep, a
 * bigger judge or screen deadline, or narrower screen waves.
 *
 * COST, stated: a recovered outage stays open up to 75 minutes past its last
 * held tick, carrying its last snapshot, so #5121's resolved notice arrives that
 * much later; `last_held_at` is what a renderer reads to say the work was last
 * held at T. A pick (#5118) closes at once with `picked`. OPENING has no grace —
 * it is immediate; any debounce of a customer-facing notice belongs to its owner,
 * which reads `opened_at`.
 */
export const KEY_OUTAGE_DRAIN_GRACE_MS = RAW_TASK_LEASE_MS + 60 * 60_000;

/** What {@link planKeyOutage} was handed: the stored open row (if any), this tick's reads, and the tick's clock. */
export type PlanKeyOutageInput = {
  /** The account's open outage, or null — `findOpenKeyOutage`'s answer. */
  open: KeyOutage | null;
  /** Every counted held item this tick read — `listKeyOutageHeldWork`'s answer. */
  held: readonly KeyOutageHeldItem[];
  /** The pick candidates per held step — `keyOutagePickCandidates`' answer. */
  usable: readonly KeyOutageUsable[];
  now: Date;
};

/** The pure verdict #5116's lane executes — one of four answers, no IO here. */
export type KeyOutagePlan =
  | { kind: 'open'; snapshot: KeyOutageSnapshot }
  | { kind: 'update'; snapshot: KeyOutageSnapshot; changed: boolean }
  | { kind: 'close'; reason: KeyOutageCloseReason }
  | { kind: 'none' };

/**
 * Decide one tick of the outage lifecycle. Pure; #5116's lane is the IO shell
 * that executes the verdict (`openKeyOutage` / `updateKeyOutage` /
 * `closeKeyOutage`, all CAS-shaped so a losing racer's verdict degrades to
 * `none` rather than double-writing):
 *
 * - no open outage, held work: OPEN with the canonical snapshot;
 * - open, held work: UPDATE — `changed` compares the canonical-sorted snapshot
 *   to the stored one, and the lane moves `updated_at` (and #5121's edited
 *   notice) only on a change; `last_held_at` moves every tick regardless;
 * - open, held work empty, `now - last_held_at >= {@link
 *   KEY_OUTAGE_DRAIN_GRACE_MS}`: CLOSE `drained`;
 * - open inside the grace, or nothing held and nothing open: NONE.
 */
export function planKeyOutage(input: PlanKeyOutageInput): KeyOutagePlan {
  const snapshot: KeyOutageSnapshot = {
    held: sortKeyOutageHeld(input.held),
    usable: [...input.usable],
  };
  if (snapshot.held.length > 0) {
    if (input.open === null) return { kind: 'open', snapshot };
    return {
      kind: 'update',
      snapshot,
      changed: canonicalJson(snapshot) !== canonicalJson(input.open.snapshot),
    };
  }
  if (input.open === null) return { kind: 'none' };
  const sinceHeld = input.now.getTime() - input.open.lastHeldAt.getTime();
  if (sinceHeld >= KEY_OUTAGE_DRAIN_GRACE_MS) {
    return { kind: 'close', reason: KeyOutageCloseReason.Drained };
  }
  return { kind: 'none' };
}

/**
 * Key-order-insensitive JSON, so the `changed` test compares CONTENT, not the
 * order zod happened to emit keys in. Both sides are parsed snapshots: no
 * `undefined`, no dates — every leaf is a string, number, boolean or null.
 */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const rec = value as Record<string, unknown>;
    const body = Object.keys(rec)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(rec[k]) ?? 'null'}`)
      .join(',');
    return `{${body}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * The result of an owner's pick (#5118, epic #5099 D3): the verdict both this
 * REST door and #5125's MCP `billing outage_pick` action return, so the two
 * surfaces diagnose one fact in one vocabulary. The 6-armed discriminated
 * union is the contract — every handler renders through a `switch` on `kind`,
 * never `if (!ok)`.
 *
 * - `picked` — the pick closed the outage and pinned every still-held task /
 *   ask of its snapshot onto the named key; `tasks` and `asks` are the
 *   counts the write moved (a row that left `pending` meanwhile or whose
 *   window is now null is skipped by the CAS and not counted).
 * - `no_open_outage` — there is no row to pick; the read of the snapshot
 *   answered null.
 * - `unknown_key_label` — no non-revoked ORG key on the account carries that
 *   label. An unlabelled key cannot be named: the pin addresses keys by
 *   `key_label` only.
 * - `key_not_usable` — the named key serves SOME but not EVERY held step;
 *   `unservedSteps` names the steps that lack it. Refusing a partial pick
 *   keeps the invariant #5119/#5120/#5121 read: a pick always closes the
 *   outage `picked`.
 * - `outage_moved` — the outage closed between the read and the write; the
 *   pick lost to a concurrent close (#5116's drain, or another owner).
 * - `draining` — the snapshot holds no item. The outage is inside #5115's
 *   drain grace (`KEY_OUTAGE_DRAIN_GRACE_MS`) and closes `drained` on its
 *   own. The 409 detail names that constant's wait, never a fixed number.
 */
export type KeyOutagePickResult =
  | { kind: 'picked'; outageId: string; keyLabel: string; tasks: number; asks: number }
  | { kind: 'no_open_outage' }
  | { kind: 'unknown_key_label' }
  | { kind: 'key_not_usable'; unservedSteps: string[] }
  | { kind: 'outage_moved' }
  | { kind: 'draining' };

/**
 * The keys the pick (#5118) may apply to an outage's held work — the candidates
 * that appear in the `usable` list of EVERY step in `held`, in rank order.
 * `[]` when `held` is empty (nothing to apply, nothing to offer). Pure: this
 * is THE pick rule, stated once. Surfaces render it over the snapshot (this
 * issue's GET, #5119's picker, #5125's MCP surfaces); the pick applies it over
 * LIVE candidates. No other function restates the rule.
 *
 * The intersection is over `BYOKKeyId` ALONE, not the `(keyId, provider,
 * model)` triple — `keyOutagePickCandidates` resolves `pick.model` per step
 * through `resolveLlmFromPool` (a catalogue entry tagged for that step's
 * tier), so the SAME key legitimately carries a different model on two held
 * steps (e.g. a faster model for `coding`, a cheaper one for `triage`). The
 * key is still one pick: intersecting on the triple would drop it from the
 * pickable set and `keyPinVerdict` (which matches the pick by `keyId` alone —
 * the label resolves to one key) would then find nothing served,
 * self-contradicting a 422. `label`
 * is the wire name (`envelope.llm.key_label`, `raw_tasks.llm_pin`'s
 * `key_label`) and is what the pick actually addresses; the call site reads
 * it off the surviving candidates to build `pick_labels`.
 */
export function keyOutagePickableKeys(input: {
  held: readonly KeyOutageHeldItem[];
  usable: readonly KeyOutageUsable[];
}): KeyOutagePickCandidate[] {
  if (input.held.length === 0) return [];
  // The per-step candidate lists keyed by step — `usable` is the snapshot's
  // rank order, so a `Map` preserves it and the intersection walks it once.
  const byStep = new Map<KeyOutageStep, readonly KeyOutagePickCandidate[]>();
  for (const entry of input.usable) {
    byStep.set(entry.step, entry.keys);
  }
  // The seed is the FIRST held step's ordered list; the surviving candidates
  // are the ones every other step ALSO lists, by `keyId` alone.
  const first = input.held[0];
  if (first === undefined) return [];
  const seed = byStep.get(first.step) ?? [];
  const remaining = input.held.slice(1);
  return seed.filter((candidate) =>
    remaining.every((item) =>
      (byStep.get(item.step) ?? []).some((other) => other.keyId === candidate.keyId),
    ),
  );
}

/**
 * The label + usability verdict ONE builder gives BOTH doors that name a key by
 * label — the key-outage pick (`pickKeyOutageFlow`, `apps/api/src/routes/
 * key-outage.ts`) and the blocker answer's model pin (`resolveKeyPin`,
 * `apps/api/src/deps/key-pin.ts`). Three answers, no IO here:
 *
 * - `usable` — the label resolved (when the pin names one) and EVERY step in
 *   `steps` lists a candidate the pin lands on; `key` is the key the pick
 *   applies (the labelled one, or the first serving pick's key when the pin
 *   names none) and `candidates` is the per-step list the pick applies from;
 * - `unknown_key_label` — no key in `labelKeys` carries `pin.key_label`;
 * - `key_not_usable` — `unservedSteps` names every step whose candidate list
 *   holds no pick the pin lands on, in the caller's step order (the held
 *   snapshot's canonical order at both call sites).
 */
export type KeyPinVerdict =
  | { kind: 'usable'; key: BYOKKey; candidates: readonly KeyOutageUsable[] }
  | { kind: 'unknown_key_label'; keyLabel: string }
  | { kind: 'key_not_usable'; unservedSteps: string[] };

/**
 * THE ONE PIN VERDICT — #6521's permissive write-side predicate was retired
 * here, not kept beside it: two doors answering one question ("could this work
 * run on that key") from different data meant a pin the pick refused was still
 * storable. The pick's semantics won, so the candidates come from
 * {@link keyOutagePickCandidates} over `poolKeys` + `cooledKeyIds` — the
 * EXISTING grader (active, `status = 'valid'`, verified, not Redis-cooled, lane
 * fence, earmark, model parked), never a second predicate. A key cooling right
 * now is therefore not a candidate, and a donated key never is (`ossEligible:
 * false`, `findKeyFor`'s fail-closed default). The per-step serves check is the
 * `pinLandsOn` rule: a candidate serves the pin when it satisfies every axis
 * the pin NAMED — an axis the pin left out is free — and a step is served when
 * one of its candidates does. Label resolution runs against `labelKeys` (the
 * non-revoked ORG keys, `pinnableAccountKeys`) BEFORE the pool is ranked —
 * inside this pure function. The IO seam above it reads the other way: before
 * calling, `resolveKeyPin` (`apps/api/src/deps/key-pin.ts`) has already awaited
 * `livePool`, so an unknown label DOES cost the pool + cooldown read, and a
 * faulting read answers `service_unavailable` (503) before the label resolves.
 */
export function keyPinVerdict(input: {
  labelKeys: readonly BYOKKey[];
  poolKeys: readonly BYOKKey[];
  cooledKeyIds?: readonly string[] | undefined;
  pin: TaskLlmPreference;
  steps: readonly KeyOutageStep[];
}): KeyPinVerdict {
  const label = input.pin.key_label;
  let named: BYOKKey | undefined;
  if (label !== undefined) {
    named = input.labelKeys.find((k) => k.label === label);
    if (named === undefined) return { kind: 'unknown_key_label', keyLabel: label };
  }
  const candidates = keyOutagePickCandidates({
    keys: input.poolKeys,
    cooledKeyIds: input.cooledKeyIds,
    steps: input.steps,
  });
  // The unserved steps are the steps whose candidate list does NOT carry a pick
  // the pin lands on — every step the pin missed, in the caller's order.
  const unserved: string[] = [];
  for (const step of input.steps) {
    const entry = candidates.find((u) => u.step === step);
    const listed = entry?.keys.some((c) => pinLandsOn(c, input.pin)) ?? false;
    if (!listed && !unserved.includes(step)) unserved.push(step);
  }
  if (unserved.length > 0) return { kind: 'key_not_usable', unservedSteps: unserved };
  const firstServed = candidates.flatMap((u) => u.keys).find((c) => pinLandsOn(c, input.pin));
  const key =
    named ??
    (firstServed === undefined
      ? undefined
      : input.poolKeys.find((k) => (k.id as string) === firstServed.keyId)) ??
    input.poolKeys[0];
  if (key === undefined) {
    // Unreachable through all three doors: the pick always names a `key_label` (its
    // body schema requires it), the answer door always passes [KEY_OUTAGE_ASK_STEP], and
    // the plan-model door (#7012, `apps/api/src/deps/plan-model.ts`) never calls with no
    // step and reads `usable` only as "served", so `named` or `firstServed` is set. Kept loud —
    // `usable.key` is the pick's audit-row actor, and inventing one would put
    // a lie in the trail.
    throw new Error(
      'keyPinVerdict: usable carries no key — no named label, no served pick, empty pool',
    );
  }
  return { kind: 'usable', key, candidates };
}

/**
 * Does this CANDIDATE satisfy every axis the pin named? An axis the pin left
 * out is free. The candidate-flavoured form of the rank rule retired from
 * `llm-pin-usable.ts` with #6521's permissive predicate: a candidate already IS
 * one `(key, model)` pick, so the rule reads the flattened
 * `label`/`provider`/`model`.
 */
function pinLandsOn(candidate: KeyOutagePickCandidate, pin: TaskLlmPreference): boolean {
  if (pin.key_label !== undefined && candidate.label !== pin.key_label) return false;
  if (pin.provider !== undefined && candidate.provider !== pin.provider) return false;
  return pin.model === undefined || candidate.model === pin.model;
}

/**
 * The ids a pick (#5118) writes — the held set split by kind for
 * `applyKeyOutagePick`. Extracted from the retired pick verdict's
 * apply arm: the route checks `held.length === 0` (draining) itself, then calls
 * this for the write, so the draining arm is a read of the snapshot the route
 * already holds rather than a second verdict shape.
 */
export function keyOutageApplySet(held: readonly KeyOutageHeldItem[]): {
  taskIds: TaskId[];
  askIds: RawTaskId[];
} {
  return {
    taskIds: held.filter((h): h is KeyOutageHeldTask => h.kind === 'task').map((h) => h.id),
    askIds: held.filter((h): h is KeyOutageHeldAsk => h.kind === 'ask').map((h) => h.id),
  };
}
